import React from "react";
import {
  Box,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Button,
  Typography,
  Chip,
  Tooltip,
  TableSortLabel,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { useGetDealersDirectQuery, useGetDmsListQuery, useGetDealerLocationsQuery } from "../../app/services/dealer";
import DealerDetailsDialog from "./components/DealerDetailsDialog";
import { mapDmsFilterToEnumValue, DMS_ENUM_MAP } from "../../util/dmsMapping";

const DealersPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = React.useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = React.useState("");
  const [dmsType, setDmsType] = React.useState<string>("All");
  const [page, setPage] = React.useState(0); // 0-based UI
  const [pageSize, setPageSize] = React.useState(25);
  const [isSearching, setIsSearching] = React.useState(false);

  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const [selectedDealerId, setSelectedDealerId] = React.useState<number | undefined>(undefined);
  const [selectedDealerName, setSelectedDealerName] = React.useState<string | undefined>(undefined);
  const [expandedRows, setExpandedRows] = React.useState<Record<number, boolean>>({});

  const location = useLocation();
  const navigate = useNavigate();
  const search = React.useMemo(() => new URLSearchParams(location.search), [location.search]);
  const productIdFilter = search.get("productId") || undefined;
  const productNameFilter = search.get("productName") || undefined;
  const locationCountFilter = search.get("locationCount") ? parseInt(search.get("locationCount")!, 10) : undefined;

  const { data: dmsList } = useGetDmsListQuery();

  const toDmsName = React.useCallback((value: any): string => {
    if (value == null) return "-";
    if (typeof value === 'string' && isNaN(parseInt(value, 10))) return value;
    const num = typeof value === 'number' ? value : parseInt(String(value), 10);
    if (Number.isNaN(num)) return String(value);
    const entry = Object.entries(DMS_ENUM_MAP).find(([_, v]) => v === num);
    return entry ? entry[0] : String(value);
  }, []);

  // Debounce search query
  React.useEffect(() => {
    setIsSearching(true);
    const delayDebounce = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery.trim());
      setIsSearching(false);
    }, 500); // 500ms debounce

    return () => clearTimeout(delayDebounce);
  }, [searchQuery]);

  const queryArgs: GetDealership = React.useMemo(() => ({
    dmsTypes: mapDmsFilterToEnumValue(dmsType),
    searchText: debouncedSearchQuery,
    page: page + 1, 
    pageSize,
    includeIsTest: true,
    case: "All",
    ...(productIdFilter ? { productId: productIdFilter } : {}),
    ...(locationCountFilter ? { locationCount: locationCountFilter } : {}),
  }), [dmsType, debouncedSearchQuery, page, pageSize, productIdFilter, locationCountFilter]);

  const { data: dealersResp, isFetching, isError } = useGetDealersDirectQuery(queryArgs, {
    refetchOnMountOrArgChange: true,
    refetchOnReconnect: true,
  });

  const dealers = dealersResp?.data ?? [];
  const totalCount = dealersResp?.totalRecordsCount ?? 0;

  type SortKey = 'dmsDealerId' | 'name' | 'locations' | 'users';
  const [sortBy, setSortBy] = React.useState<SortKey>('dmsDealerId');
  const [sortDir, setSortDir] = React.useState<'asc' | 'desc'>('asc');
  const toggleSort = (key: SortKey) => {
    setSortBy((prev) => {
      if (prev === key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortDir('asc');
      return key;
    });
  };

  const sortedDealers = React.useMemo(() => {
    const list = [...dealers];
    const dir = sortDir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      const valA = a[sortBy];
      const valB = b[sortBy];
      if (valA == null && valB == null) return 0;
      if (valA == null) return dir;
      if (valB == null) return -dir;
      if (typeof valA === 'number' && typeof valB === 'number') return (valA - valB) * dir;
      return String(valA).localeCompare(String(valB)) * dir;
    });
    return list;
  }, [dealers, sortBy, sortDir]);

  const handleClearSearch = () => {
    setSearchQuery("");
    setPage(0);
  };

  const clearProductFilter = () => {
    navigate('/dealers');
  };

  const clearLocationFilter = () => {
    const params = new URLSearchParams(location.search);
    params.delete('locationCount');
    navigate(`/dealers${params.toString() ? '?' + params.toString() : ''}`);
  };

  const toggleLocations = (dealer: any) => {
    const idNum = typeof dealer.dealerId === "string" ? parseInt(dealer.dealerId, 10) : dealer.dealerId;
    if (Number.isNaN(idNum)) return;
    setExpandedRows((prev) => ({ ...prev, [idNum]: !prev[idNum] }));
  };

  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const openDetails = (dealer: any) => {
    const idNum = typeof dealer.dealerId === "string" ? parseInt(dealer.dealerId, 10) : dealer.dealerId;
    if (Number.isNaN(idNum)) return;
    const name = dealer.companyName?.trim() ? dealer.companyName : dealer?.name;
    setSelectedDealerId(idNum);
    setSelectedDealerName(name);
    setDetailsOpen(true);
  };

  return (
    <Box sx={{ pt: 1, pb: 2, px: { xs: 0.5, sm: 1 } }}>
      <Paper sx={{ p: 2, mb: 2 }}>
        {locationCountFilter && (
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={`Showing dealers with ${locationCountFilter} ${locationCountFilter === 1 ? 'location' : 'locations'}`}
              onDelete={clearLocationFilter}
              color="primary"
              variant="outlined"
            />
            <Typography variant="caption" color="text.secondary">
              ({totalCount} dealers)
            </Typography>
          </Box>
        )}
        {productIdFilter && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Showing dealers enrolled for product{productNameFilter ? ':' : ''}
              {productNameFilter && (<Chip size="small" color="primary" label={productNameFilter} />)}
            </Typography>
            <Button size="small" variant="outlined" onClick={clearProductFilter} sx={{ textTransform: 'none' }}>Clear filter</Button>
          </Box>
        )}
        <Box sx={{ display: "flex", gap: 2, flexWrap: { xs: "wrap", sm: "nowrap" }, alignItems: "center" }}>
          <TextField
            placeholder="Search by Dealer Name, Dealer Number, or DMS Dealer ID"
            size="small"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(0); // Reset to first page on new search
            }}
            sx={{ flexGrow: 1, minWidth: 280 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {isSearching ? <CircularProgress size={20} /> : <SearchIcon />}
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton aria-label="clear search" onClick={handleClearSearch} edge="end">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            select
            label="Select DMS"
            size="small"
            value={dmsType}
            onChange={(e) => {
              setDmsType(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 220 }}
          >
            <MenuItem key="All" value="All">All</MenuItem>
            {(dmsList ?? []).map((d: any) => (
              <MenuItem key={d.id} value={d.name}>{d.name}</MenuItem>
            ))}
          </TextField>
        </Box>
      </Paper>

      <Paper sx={{ overflowX: { xs: 'auto', md: 'visible' } }}>
        <TableContainer sx={{ maxHeight: 560 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sortDirection={sortBy === 'dmsDealerId' ? sortDir : false as any}>
                  <TableSortLabel
                    active={sortBy === 'dmsDealerId'}
                    direction={sortBy === 'dmsDealerId' ? sortDir : 'asc'}
                    onClick={() => toggleSort('dmsDealerId')}
                  >
                    DMS Dealer ID
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: { xs: 200, md: 320, lg: 420 } }} sortDirection={sortBy === 'name' ? sortDir : false as any}>
                  <TableSortLabel
                    active={sortBy === 'name'}
                    direction={sortBy === 'name' ? sortDir : 'asc'}
                    onClick={() => toggleSort('name')}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>DMS</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, width: 160, maxWidth: 160 }}>Server</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Address</TableCell>
                <TableCell sortDirection={sortBy === 'locations' ? sortDir : false as any}>
                  <TableSortLabel
                    active={sortBy === 'locations'}
                    direction={sortBy === 'locations' ? sortDir : 'asc'}
                    onClick={() => toggleSort('locations')}
                  >
                    Locations
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === 'users' ? sortDir : false as any}>
                  <TableSortLabel
                    active={sortBy === 'users'}
                    direction={sortBy === 'users' ? sortDir : 'asc'}
                    onClick={() => toggleSort('users')}
                  >
                    Users
                  </TableSortLabel>
                </TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isFetching ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : isError ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="error">Failed to load dealers.</Typography>
                  </TableCell>
                </TableRow>
              ) : dealers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No dealers found
                  </TableCell>
                </TableRow>
              ) : (
                sortedDealers.flatMap((dealer: any) => {
                  const idNum = typeof dealer.dealerId === "string" ? parseInt(dealer.dealerId, 10) : dealer.dealerId;
                  const rows: JSX.Element[] = [];
                  rows.push(
                    <TableRow key={idNum} hover>
                      <TableCell>
                        <Tooltip title={String(dealer.dmsDealerId ?? '-')} placement="top" arrow>
                          <Button
                            variant="text"
                            color="primary"
                            onClick={() => openDetails(dealer)}
                            sx={{
                              textTransform: 'none',
                              p: 0,
                              minWidth: 0,
                              textDecoration: 'underline',
                              '&:hover': { backgroundColor: 'transparent' },
                            }}
                          >
                            <Typography variant="body2" noWrap>
                              {dealer.dmsDealerId ?? '-'}
                            </Typography>
                          </Button>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="left" sx={{ textAlign: 'left' }}>
                        <Tooltip
                          title={(dealer.companyName?.trim() ? dealer.companyName : dealer?.name) || '-'}
                          placement="top"
                          arrow
                        >
                          <Button
                            variant="text"
                            color="primary"
                            onClick={() => openDetails(dealer)}
                            sx={{
                              textTransform: 'none',
                              p: 0,
                              minWidth: 0,
                              textDecoration: 'underline',
                              justifyContent: 'flex-start',
                              maxWidth: { xs: 200, md: 320, lg: 420 },
                              width: '100%',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              '&:hover': { backgroundColor: 'transparent' },
                            }}
                          >
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {dealer.companyName?.trim() ? dealer.companyName : dealer?.name}
                            </Typography>
                          </Button>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={toDmsName(dealer.dmsType)} variant="outlined" color="primary" />
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, width: 160, maxWidth: 160 }}>
                        <Tooltip title={dealer.server ?? '-'} placement="top" arrow>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 140 }}>
                            {dealer.server ?? '-'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Tooltip title={dealer.address ?? '-'} placement="top" arrow>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 340 }}>
                            {dealer.address ?? '-'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={String(dealer.locations ?? 0)} color="primary" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={String(dealer.users ?? 0)} color="primary" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant={expandedRows[idNum] ? 'contained' : 'outlined'}
                          onClick={() => toggleLocations(dealer)}
                          sx={{ textTransform: 'none' }}
                        >
                          {expandedRows[idNum] ? 'Hide Locations' : 'Locations'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );

                  if (expandedRows[idNum]) {
                    rows.push(
                      <DealerLocationsRow key={`loc-${idNum}`} dealerId={idNum} colSpan={8} />
                    );
                  }

                  return rows;
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={pageSize}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[25, 50, 75, 100]}
        />
      </Paper>

      <DealerDetailsDialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        dealerId={selectedDealerId}
        dealerName={selectedDealerName}
      />
    </Box>
  );
};

export default DealersPage;

const DealerLocationsRow: React.FC<{ dealerId: number; colSpan: number }> = ({ dealerId, colSpan }) => {
  const { data: locations, isFetching, isError } = useGetDealerLocationsQuery(dealerId);

  return (
    <TableRow>
      <TableCell colSpan={colSpan} sx={{ backgroundColor: 'background.default', p: 0 }}>
        <Box sx={{ p: 2 }}>
          {isFetching ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">Loading locations…</Typography>
            </Box>
          ) : isError ? (
            <Typography color="error">Failed to load locations.</Typography>
          ) : !locations || locations.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No other locations found for this dealer.</Typography>
          ) : (
            <Table size="small" sx={{ backgroundColor: 'background.paper' }}>
              <TableHead>
                <TableRow>
                  <TableCell width={140}>Dealer ID</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Server</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {locations.map((loc) => (
                  <TableRow key={loc.dealerId}>
                    <TableCell>
                      <Typography variant="body2">{loc.dealerId}</Typography>
                      {loc.isRoot && <Chip size="small" color="primary" label="Root" sx={{ ml: 1 }} />}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{loc.name ?? '-'}</Typography>
                      <Typography variant="caption" display="block" color="text.secondary">{loc.address ?? '-'}</Typography>
                    </TableCell>
                    <TableCell>{loc.server ?? '-'}</TableCell>
                    <TableCell>
                      <Chip size="small" label={loc.isActive ? 'Active' : 'Inactive'} color={loc.isActive ? 'success' : 'default'} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      </TableCell>
    </TableRow>
  );
};
